import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Box, ImagePlus, Mic, Pencil, Sparkles, Star, Trash2, Wand2, X } from "lucide-react";
import api, { API_BASE, getAccessToken, getSessionId } from "../lib/api";
import ArtiVoicePanel, { useArtiDeskVoice } from "../components/ArtiVoicePanel";
import { useToasts } from "../components/Toasts";
import {
  buildDeskGenerationPayload,
  canContinueEditing,
  currentGenerationId,
  deskIterations,
  deskReference,
  deskReferences,
  deskSources,
  deskStyleValue,
  meshSessionSource,
  DESK_STYLE_CUSTOM,
  DESK_STYLE_PRESETS,
  generations,
  isReferenceGeneration,
  iterationKindLabel,
  iterationScore,
  hubProductPath,
  PIPELINE_STAGES,
  pipelineStage,
  pipelineStageLabel,
  recordPreviewPlan,
  resolveDeskStyleChoice,
  loopHubPath,
} from "../lib/forgelinkPathway";
import {
  ARTI_ASK_EVENT,
  ARTI_TALK_EVENT,
  artiAck,
  buildArtiChatPayload,
  matchArtiDeskCommand,
  suggestedDesignPrompt,
} from "../lib/artiDesk";
import { appendTranscript, cancelSpeech, speechSupported, startSpeechRecognition } from "../lib/speechPrompt";
import AuthImage from "../components/AuthImage";
import ImageInspectViewport from "../components/ImageInspectViewport";
import MeshInspectViewport from "../components/MeshInspectViewport";
import { MESH_UPLOAD_LIMIT, classifyMeshName } from "../lib/meshInspect";
import { FORGELINC_NAME } from "../lib/productNames";
import { DesignAttachForm } from "./ForgeLinkPathway";
import "./ForgeLinkDesignDesk.css";
import "./BusinessWorkspacePage.css";
import "./BusinessWorkspaceForms.css";

const BUSINESS_KEY = "linkforge";
const GENERATE_TIMEOUT_MS = 180000;
const RENDERER_LABELS = {
  openai: { mode: "live", label: "Live · OpenAI" },
  replicate: { mode: "live", label: "Live · Replicate" },
  stability: { mode: "live", label: "Live · Stability" },
  stub: { mode: "stub", label: "Stub renderer" },
  upload: { mode: "upload", label: "Uploaded reference" },
};

function snippet(prompt, length = 72) {
  const text = String(prompt || "").trim();
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

function apiDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const msg = detail.detail || detail.message || detail.error;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  if (Array.isArray(detail)) {
    const msg = detail.map((row) => row?.msg || row?.detail || "").filter(Boolean).join(" ");
    if (msg) return msg;
  }
  if (err?.code === "ECONNABORTED") return "The image request timed out. The last good iteration is still on the desk — retry Generate or Continue editing.";
  return fallback;
}

function badgeFor(configured, selected, forceStub) {
  const actual = selected?.renderer;
  if (actual && RENDERER_LABELS[actual]) return RENDERER_LABELS[actual];
  if (actual) {
    return actual === "stub"
      ? { mode: "stub", label: "Stub renderer" }
      : { mode: "live", label: `Live · ${actual}` };
  }
  if (forceStub) return { mode: "stub", label: "Stub renderer" };
  return configured || { mode: "unconfigured", label: "No image API key" };
}

function pillTone(mode) {
  if (mode === "live") return "green";
  if (mode === "unconfigured") return "red";
  if (mode === "upload") return "cyan";
  return "cyan";
}

function InputStrip({
  title,
  hint,
  items,
  busy,
  armed,
  testId,
  fileTestId,
  onFiles,
  onDropGeneration,
  onPrimary,
  onRemove,
}) {
  const [hot, setHot] = useState(false);
  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = event.dataTransfer.types.includes("Files") ? "copy" : "move";
    setHot(true);
  };
  const onDrop = (event) => {
    event.preventDefault();
    setHot(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) {
      onFiles(files);
      return;
    }
    const generationId = event.dataTransfer.getData("text/forgelink-generation") || event.dataTransfer.getData("text/plain");
    if (generationId) onDropGeneration(generationId);
  };
  return (
    <div
      className={`fld-slot${armed ? " is-armed" : ""}${hot ? " is-hot" : ""}${items.length ? " has-image" : ""}`}
      data-testid={testId}
      onDragOver={onDragOver}
      onDragLeave={() => setHot(false)}
      onDrop={onDrop}
    >
      <div className="fld-slot-head">
        <span>
          <small>{title}</small>
          <b>{items.length ? `${items.length} image${items.length === 1 ? "" : "s"} · first is primary` : hint}</b>
        </span>
        <label className="fld-slot-add">
          Add
          <input
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            multiple
            disabled={busy}
            data-testid={fileTestId}
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              event.target.value = "";
              if (files.length) onFiles(files);
            }}
          />
        </label>
      </div>
      <div className="fld-thumbs">
        {items.length ? items.map((row, index) => (
          <div key={row.id} className={`fld-thumb${index === 0 ? " is-primary" : ""}`} data-testid={`${testId}-item`}>
            <button
              type="button"
              className="fld-thumb-pick"
              disabled={busy}
              title={index === 0 ? "Primary" : "Make primary"}
              onClick={() => onPrimary(row)}
            >
              <AuthImage
                path={row.previewPath}
                alt={snippet(row.prompt, 40)}
                placeholderClassName="fld-ph"
              />
              {index === 0 ? <em>Primary</em> : null}
            </button>
            <button
              type="button"
              className="fld-thumb-x"
              disabled={busy}
              aria-label={`Remove ${title.toLowerCase()} image`}
              onClick={() => onRemove(row)}
            >
              <X size={10} />
            </button>
          </div>
        )) : <span className="fld-slot-empty">Drop PNG or JPG images</span>}
      </div>
    </div>
  );
}

function ScoreStars({ value, disabled, onChange }) {
  const current = iterationScore({ score: value });
  return (
    <span className="fld-stars" data-testid="forgelink-desk-score">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          type="button"
          key={star}
          className={star <= current ? "is-on" : ""}
          disabled={disabled}
          aria-label={`Score ${star} of 5`}
          onClick={(event) => {
            event.stopPropagation();
            onChange?.(star);
          }}
        >
          <Star size={11} fill={star <= current ? "currentColor" : "none"} />
        </button>
      ))}
    </span>
  );
}

function DeskCardMedia({ record }) {
  const plan = recordPreviewPlan(BUSINESS_KEY, record);
  const [index, setIndex] = useState(0);
  const candidate = plan.images[index];
  if (!candidate) {
    if (plan.mesh) {
      return (
        <span className="fld-card-fallback" data-testid="forgelink-desk-card-mesh">
          <Box size={22} />
          <b>3D</b>
          <small>{plan.mesh.filename}</small>
        </span>
      );
    }
    return <span className="fld-card-fallback">No image yet</span>;
  }
  return (
    <AuthImage
      path={candidate.path}
      src={candidate.externalUrl}
      alt={record?.title || "Product image"}
      className="fld-card-img"
      placeholderClassName="fld-card-fallback"
      loadingLabel=""
      errorLabel=""
      onError={() => setIndex((current) => current + 1)}
    />
  );
}

function visibleDeskRecords(rows) {
  return (rows || []).filter((row) => row?.status !== "archived" && !row?.metadata?.archived);
}

export default function ForgeLinkDesignDesk() {
  const toast = useToasts((s) => s.push);
  const [params, setParams] = useSearchParams();
  const recordId = params.get("record") || "";
  const forceStub = params.get("force_stub") === "1";
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(null);
  const [renderer, setRenderer] = useState({ mode: "unconfigured", label: "No image API key" });
  const [prompt, setPrompt] = useState("");
  const [textOverlay, setTextOverlay] = useState("");
  const [stylePreset, setStylePreset] = useState("");
  const [styleCustom, setStyleCustom] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [editFromSource, setEditFromSource] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [error, setError] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [lastFailedMode, setLastFailedMode] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [outputMode, setOutputMode] = useState("image");
  const [inputOpen, setInputOpen] = useState(false);
  const [stripOpen, setStripOpen] = useState(false);
  const [viewerFocus, setViewerFocus] = useState(false);
  const [avatarsHidden, setAvatarsHidden] = useState(true);
  const viewerRef = useRef(null);
  const [startError, setStartError] = useState("");
  const [editor, setEditor] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStage, setEditStage] = useState("design");
  const [editError, setEditError] = useState("");
  const [pendingDelete, setPendingDelete] = useState([]);
  const [deleteError, setDeleteError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [talk, setTalk] = useState("");
  const [messages, setMessages] = useState([]);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [suggestGenerate, setSuggestGenerate] = useState(false);
  const [listening, setListening] = useState(false);
  const [micSupported] = useState(() => speechSupported());
  const recognitionRef = useRef(null);
  const aliveRef = useRef(true);
  const promptRef = useRef("");
  const talkRef = useRef("");
  const messagesRef = useRef([]);
  const selectedRef = useRef(null);
  const { voiceOn, setVoiceOn, line: artiLine, speaking: artiSpeaking, speak: speakArti } = useArtiDeskVoice();
  promptRef.current = prompt;
  talkRef.current = talk;
  messagesRef.current = messages;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      try { recognitionRef.current?.stop?.(); } catch { /* speech engine may already be gone */ }
    };
  }, []);

  useEffect(() => {
    if (!recordId) {
      delete document.documentElement.dataset.deskWork;
      delete document.documentElement.dataset.deskAvatars;
      return undefined;
    }
    document.documentElement.dataset.deskWork = "1";
    if (avatarsHidden) document.documentElement.dataset.deskAvatars = "off";
    else delete document.documentElement.dataset.deskAvatars;
    return () => {
      delete document.documentElement.dataset.deskWork;
      delete document.documentElement.dataset.deskAvatars;
    };
  }, [recordId, avatarsHidden]);

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setViewerFocus(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleViewerFocus = async () => {
    const next = !viewerFocus;
    setViewerFocus(next);
    const node = viewerRef.current;
    if (!node) return;
    try {
      if (next && !document.fullscreenElement) await node.requestFullscreen();
      if (!next && document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* The focus layout still fills the desk when fullscreen is blocked. */
    }
  };

  const loadRecords = useCallback(async () => {
    const { data } = await api.get(`/businesses/${BUSINESS_KEY}/records`);
    const rows = visibleDeskRecords(Array.isArray(data) ? data : []);
    setRecords(rows);
    setSelectedIds((current) => current.filter((id) => rows.some((row) => row.id === id)));
    return rows;
  }, []);

  const openEditor = (row) => {
    setEditError("");
    setEditTitle(row.title || "");
    setEditDescription(row.description || "");
    setEditStage(pipelineStage(row));
    setEditor(row);
  };

  const saveEditor = async (event) => {
    event.preventDefault();
    if (!editor?.id) return;
    setBusy(true);
    setEditError("");
    try {
      await api.patch(`/businesses/${BUSINESS_KEY}/records/${editor.id}/info`, {
        title: editTitle.trim(),
        description: editDescription,
        stage: editStage,
      });
      setEditor(null);
      await loadRecords();
      toast("Product info saved.", "ok");
    } catch (err) {
      setEditError(apiDetail(err, "Could not save that product."));
    } finally {
      setBusy(false);
    }
  };

  const askDelete = (rows) => {
    const targets = (rows || []).filter((row) => row?.id);
    if (!targets.length) return;
    setDeleteError("");
    setPendingDelete(targets);
  };

  const confirmDelete = async () => {
    if (!pendingDelete.length) return;
    setBusy(true);
    setDeleteError("");
    try {
      for (const row of pendingDelete) {
        await api.delete(`/businesses/${BUSINESS_KEY}/records/${row.id}`);
      }
      const gone = new Set(pendingDelete.map((row) => row.id));
      setRecords((current) => current.filter((row) => !gone.has(row.id)));
      setSelectedIds((current) => current.filter((id) => !gone.has(id)));
      setPendingDelete([]);
      toast(pendingDelete.length === 1 ? "Product removed from the list." : `${pendingDelete.length} products removed from the list.`, "ok");
    } catch (err) {
      setDeleteError(apiDetail(err, "Could not delete that product."));
    } finally {
      setBusy(false);
    }
  };

  const applyDesk = useCallback((payload, fallbackRecord) => {
    const nextRecord = payload?.record || fallbackRecord;
    if (payload?.renderer) setRenderer(payload.renderer);
    if (nextRecord) setRecord(nextRecord);
    return nextRecord;
  }, []);

  const loadDesk = useCallback(async (id) => {
    if (!id) return null;
    const qs = forceStub ? "?force_stub=1" : "";
    const [{ data: rec }, { data: desk }] = await Promise.all([
      api.get(`/businesses/${BUSINESS_KEY}/records/${id}`),
      api.get(`/businesses/${BUSINESS_KEY}/records/${id}/generations${qs}`),
    ]);
    applyDesk(desk, rec);
    const nextRecord = desk?.record || rec;
    const outputs = deskIterations(nextRecord);
    const current = currentGenerationId(nextRecord);
    const currentIsOutput = outputs.some((row) => row.id === current);
    setSelectedId(currentIsOutput ? current : (outputs[outputs.length - 1]?.id || null));
    setEditFromSource(!outputs.length && deskSources(nextRecord).length > 0);
    return rec;
  }, [applyDesk, forceStub]);

  useEffect(() => {
    let alive = true;
    async function boot() {
      setError(null);
      try {
        const rows = await loadRecords();
        if (!alive) return;
        if (recordId) {
          await loadDesk(recordId);
          return;
        }
        if (rows.length === 1) {
          setParams({ record: rows[0].id }, { replace: true });
        }
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || "Design Desk could not load this product.");
      }
    }
    boot();
    return () => { alive = false; };
  }, [recordId, loadRecords, loadDesk, setParams]);

  useEffect(() => {
    const source = meshSessionSource(record);
    if (source && !deskIterations(record).length) setOutputMode("mesh");
  }, [record]);

  const startFromMesh = async (fileList) => {
    const file = fileList?.[0];
    if (!file || busy) return;
    const info = classifyMeshName(file.name);
    if (!info.format) {
      setStartError(info.reason === "sliced"
        ? "Sliced .gcode.3mf files are toolpaths, not a Design Desk source. Use an STL, GLB, or model 3MF."
        : "Use an STL, GLB, or 3MF file.");
      return;
    }
    if (file.size > MESH_UPLOAD_LIMIT) {
      setStartError("This file is over the 32 MB limit.");
      return;
    }
    setBusy(true);
    setBusyKind("upload");
    setStartError("");
    setError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("title", file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "));
      const headers = {};
      const token = getAccessToken();
      const sessionId = getSessionId();
      if (token) headers.Authorization = `Bearer ${token}`;
      if (sessionId) headers["X-Session-Id"] = sessionId;
      const response = await fetch(`${API_BASE}/businesses/${BUSINESS_KEY}/records/from-mesh`, {
        method: "POST",
        headers,
        body,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = payload?.detail;
        throw new Error(typeof detail === "string" && detail.trim() ? detail : "Could not start from this 3D file.");
      }
      if (!aliveRef.current) return;
      setRecord(payload.record);
      setOutputMode("mesh");
      setParams({ record: payload.record.id });
      await loadRecords();
      toast("Started from the 3D file. Image generate is still here if you need it.", "ok");
    } catch (err) {
      if (aliveRef.current) setStartError(err.message || "Could not start from this 3D file.");
    } finally {
      clearBusy();
    }
  };

  const iters = useMemo(() => deskIterations(record), [record]);
  const sources = useMemo(() => deskSources(record), [record]);
  const references = useMemo(() => deskReferences(record), [record]);
  const reference = references[0] || deskReference(record);
  const primarySource = sources[0] || null;
  const selected = iters.find((row) => row.id === selectedId) || null;
  const currentId = currentGenerationId(record);
  const styleValue = deskStyleValue(stylePreset, styleCustom);
  const editParent = editFromSource ? (primarySource || reference || selected) : (selected || primarySource || reference);
  const canEdit = canContinueEditing(selected, reference, sources);
  selectedRef.current = selected;

  const clearBusy = () => {
    if (!aliveRef.current) return;
    setBusy(false);
    setBusyKind(null);
  };

  const generate = async (event, mode = "generate", promptOverride) => {
    event?.preventDefault?.();
    if (busy) return { ok: false };
    const nextPrompt = String(promptOverride ?? promptRef.current ?? "").trim();
    if (!record?.id || !nextPrompt) {
      if (!nextPrompt) speakArti(artiAck("need_prompt"));
      return { ok: false, reason: "need_prompt" };
    }
    if (mode === "edit" && !canContinueEditing(selected, reference, sources)) {
      const detail = "Upload a PNG/JPG reference or select an iteration that still has a preview before Continue editing image.";
      setGenerateError(detail);
      setLastFailedMode("edit");
      toast(detail, "err");
      speakArti(artiAck("need_source"));
      return { ok: false, reason: "need_source" };
    }
    if (nextPrompt !== prompt) setPrompt(nextPrompt);
    setBusy(true);
    setBusyKind(mode);
    setGenerateError(null);
    setLastFailedMode(null);
    speakArti(artiAck(mode === "edit" ? "edit" : "generate"));
    try {
      const body = buildDeskGenerationPayload({
        prompt: nextPrompt,
        mode,
        parentId: mode === "edit" ? editParent?.id : null,
        textOverlay,
        style: styleValue,
        sourceIds: sources.map((row) => row.id),
        referenceIds: references.map((row) => row.id),
      });
      const qs = forceStub ? "?force_stub=1" : "";
      const { data } = await api.post(
        `/businesses/${BUSINESS_KEY}/records/${record.id}/generations${qs}`,
        body,
        { timeout: GENERATE_TIMEOUT_MS },
      );
      if (!aliveRef.current) return { ok: false };
      const next = applyDesk(data, data?.record);
      const genId = data?.generation?.id || currentGenerationId(next);
      setSelectedId(isReferenceGeneration(data?.generation) ? selectedId : genId);
      setEditFromSource(false);
      toast(
        mode === "edit"
          ? "Edit saved. This iteration is derived from the previous image."
          : "Fresh render saved. This iteration is the current design.",
        "ok",
      );
      speakArti(artiAck(mode === "edit" ? "edit_done" : "generate_done"));
      return { ok: true, mode };
    } catch (err) {
      if (!aliveRef.current) return { ok: false };
      const detail = apiDetail(err, mode === "edit" ? "Could not continue editing the image." : "Could not generate a render.");
      setGenerateError(detail);
      setLastFailedMode(mode);
      toast(detail, "err");
      speakArti(artiAck("error"));
      return { ok: false, reason: "error", detail };
    } finally {
      clearBusy();
    }
  };

  const selectIteration = async (generation) => {
    if (!record?.id || !generation?.id || busy) return;
    setSelectedId(generation.id);
    setEditFromSource(false);
    if (generation.id === currentGenerationId(record)) return;
    setBusy(true);
    setBusyKind("select");
    try {
      const { data } = await api.post(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${generation.id}/select`);
      if (!aliveRef.current) return;
      applyDesk(data, data?.record);
      toast("This iteration is now the current design.", "ok");
    } catch (err) {
      toast(apiDetail(err, "Could not set the current design."), "err");
    } finally {
      clearBusy();
    }
  };

  const scoreIteration = async (generation, score) => {
    if (!record?.id || !generation?.id || busy) return;
    try {
      const { data } = await api.patch(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${generation.id}`, { score });
      if (!aliveRef.current) return;
      applyDesk(data, data?.record);
    } catch (err) {
      toast(apiDetail(err, "Could not save the score."), "err");
    }
  };

  const deleteIteration = async (generation) => {
    if (!record?.id || !generation?.id || busy) return;
    const ok = window.confirm("Delete this iteration from the strip? This cannot be undone.");
    if (!ok) return;
    setBusy(true);
    setBusyKind("delete");
    setGenerateError(null);
    try {
      const { data } = await api.delete(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${generation.id}`);
      if (!aliveRef.current) return;
      const next = applyDesk(data, data?.record);
      const nextOutputs = deskIterations(next);
      const nextCurrent = nextOutputs.find((row) => row.id === currentGenerationId(next))?.id || nextOutputs[nextOutputs.length - 1]?.id || null;
      setSelectedId(nextCurrent);
      setEditFromSource(!nextOutputs.length && deskSources(next).length > 0);
      toast("Iteration removed. Last remaining design (if any) is still on the desk.", "ok");
    } catch (err) {
      toast(apiDetail(err, "Could not delete that iteration."), "err");
    } finally {
      clearBusy();
    }
  };

  const refinePrompt = () => {
    if (!selected) return;
    setPrompt(selected.prompt || "");
    setTextOverlay(selected.text_overlay || "");
    const choice = resolveDeskStyleChoice(selected.style || "");
    setStylePreset(choice.presetId);
    setStyleCustom(choice.custom);
    setSelectedId(selected.id);
    setGenerateError(null);
    toast("Prompt copied. Use Continue editing image to change pixels.", "ok");
  };

  const startListening = ({ fromArti, onResult }) => {
    if (listening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* already stopping */ }
      return;
    }
    cancelSpeech();
    const recognition = startSpeechRecognition({
      onStart: () => {
        setListening(true);
      },
      onEnd: () => {
        setListening(false);
        recognitionRef.current = null;
      },
      onError: () => {
        if (fromArti) speakArti(artiAck("mic_failed"));
        else toast("Voice input failed. You can still type the prompt.", "err");
      },
      onResult,
    });
    if (!recognition) {
      if (fromArti) speakArti(artiAck("unsupported"));
      else toast("Voice input is not available in this browser. Type the prompt instead.", "err");
      return;
    }
    recognitionRef.current = recognition;
  };

  const dictatePrompt = () => {
    startListening({
      fromArti: false,
      onResult: (transcript) => {
        setPrompt((current) => appendTranscript(current, transcript));
        speakArti(artiAck("heard_prompt"));
      },
    });
  };

  const pinInput = async (generation, slot) => {
    if (!generation?.id) {
      speakArti(artiAck("need_iteration"));
      return { ok: false, reason: "need_iteration" };
    }
    if (!record?.id || busy) return { ok: false };
    setBusy(true);
    setBusyKind(slot);
    try {
      const action = slot === "source" ? "use-as-source" : "use-as-reference";
      const { data } = await api.post(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${generation.id}/${action}`);
      if (!aliveRef.current) return { ok: false };
      applyDesk(data, data?.record);
      if (slot === "source") setEditFromSource(true);
      toast(slot === "source" ? "That image is the primary source." : "That image is the primary reference.", "ok");
      if (slot === "reference") speakArti(artiAck("reference"));
      return { ok: true };
    } catch (err) {
      toast(apiDetail(err, slot === "source" ? "Could not set that image as a source." : "Could not set that image as a reference."), "err");
      speakArti(artiAck("error"));
      return { ok: false, reason: "error" };
    } finally {
      clearBusy();
    }
  };

  const promoteAsReference = (generation) => pinInput(generation, "reference");
  const promoteAsSource = (generation) => pinInput(generation, "source");

  const sendArtiChat = async (raw) => {
    const message = String(raw || "").trim();
    if (!message || busy) return { ok: false };
    cancelSpeech();
    const userTurn = { role: "user", content: message };
    const payload = buildArtiChatPayload(message, messagesRef.current);
    setMessages((current) => [...current, userTurn]);
    setTalk("");
    setBusy(true);
    setBusyKind("chat");
    try {
      const { data } = await api.post("/linkforge/arti/chat", payload);
      if (!aliveRef.current) return { ok: false };
      const replyText = String(data?.reply || "").trim() || "I'm here.";
      setMessages((current) => [...current, { role: "assistant", content: replyText }]);
      const suggested = suggestedDesignPrompt(data);
      if (suggested) {
        setPendingPrompt(suggested);
        setSuggestGenerate(Boolean(data?.suggest_generate));
      }
      speakArti(replyText);
      return { ok: true };
    } catch (err) {
      const detail = apiDetail(err, "I couldn't reach Arti on that turn.");
      if (!aliveRef.current) return { ok: false };
      setMessages((current) => [...current, { role: "assistant", content: detail }]);
      speakArti(artiAck("error"));
      toast(detail, "err");
      return { ok: false, reason: "error", detail };
    } finally {
      clearBusy();
    }
  };

  const submitArtiCommand = (raw) => {
    const text = String(raw ?? talkRef.current ?? "").trim();
    if (!text) {
      speakArti(artiAck("need_prompt"));
      return;
    }
    const parsed = matchArtiDeskCommand(text);
    setTalk("");
    if (parsed.kind === "generate") {
      if (!(parsed.prompt || promptRef.current).trim()) {
        speakArti(artiAck("need_prompt"));
        return;
      }
      return generate(undefined, "generate", parsed.prompt || undefined);
    }
    if (parsed.kind === "edit") {
      if (!(parsed.prompt || promptRef.current).trim()) {
        speakArti(artiAck("need_prompt"));
        return;
      }
      return generate(undefined, "edit", parsed.prompt || undefined);
    }
    if (parsed.kind === "reference") return promoteAsReference(selectedRef.current);
    return sendArtiChat(parsed.prompt || text);
  };

  const dictateArti = () => {
    startListening({
      fromArti: true,
      onResult: (transcript) => {
        const spoken = String(transcript || "").trim();
        if (!spoken) return;
        setTalk(spoken);
        submitArtiCommand(spoken);
      },
    });
  };
  const dictateArtiRef = useRef(dictateArti);
  dictateArtiRef.current = dictateArti;

  const sendPendingToPrompt = () => {
    const next = String(pendingPrompt || "").trim();
    if (!next) return;
    setPrompt(next);
    speakArti(artiAck("sent_to_prompt"));
    toast("Suggested prompt is in the design input.", "ok");
  };

  useEffect(() => {
    const focusTalk = () => {
      document.querySelector("[data-testid='forgelink-arti-input']")?.focus?.();
      speakArti(artiAck("hello_chat"));
    };
    const startTalk = () => dictateArtiRef.current?.();
    window.addEventListener(ARTI_ASK_EVENT, focusTalk);
    window.addEventListener(ARTI_TALK_EVENT, startTalk);
    return () => {
      window.removeEventListener(ARTI_ASK_EVENT, focusTalk);
      window.removeEventListener(ARTI_TALK_EVENT, startTalk);
    };
  }, [speakArti]);

  const uploadInputFiles = async (fileList, slot) => {
    const files = Array.from(fileList || []).filter((file) => file && (String(file.type || "").startsWith("image/") || /\.(png|jpe?g)$/i.test(file.name || "")));
    if (!files.length || !record?.id || busy) {
      if (fileList?.length && !files.length) toast("Use PNG or JPG images.", "err");
      return;
    }
    setBusy(true);
    setBusyKind("upload");
    setGenerateError(null);
    try {
      const body = new FormData();
      files.forEach((file) => body.append("files", file));
      body.append("slot", slot);
      body.append("notes", files.length === 1 ? files[0].name : "");
      const headers = {};
      const token = getAccessToken();
      const sessionId = getSessionId();
      if (token) headers.Authorization = `Bearer ${token}`;
      if (sessionId) headers["X-Session-Id"] = sessionId;
      const response = await fetch(`${API_BASE}/businesses/${BUSINESS_KEY}/records/${record.id}/generations/reference`, {
        method: "POST",
        headers,
        body,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw Object.assign(new Error(typeof payload.detail === "string" ? payload.detail : "Upload failed"), {
          response: { data: payload, status: response.status },
        });
      }
      applyDesk(payload, payload?.record);
      if (slot === "source") setEditFromSource(true);
      const label = slot === "source" ? "source" : "reference";
      toast(files.length === 1 ? `Added 1 ${label} image.` : `Added ${files.length} ${label} images.`, "ok");
    } catch (err) {
      const detail = apiDetail(err, "Could not attach those images.");
      setGenerateError(detail);
      toast(detail, "err");
    } finally {
      clearBusy();
    }
  };

  const removeInput = async (row, slot) => {
    if (!row?.id || !record?.id || busy) return;
    setBusy(true);
    setBusyKind("delete");
    try {
      const uploadOnly = isReferenceGeneration(row);
      const { data } = uploadOnly
        ? await api.delete(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${row.id}`)
        : await api.post(`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${row.id}/detach-input`, { slot });
      if (!aliveRef.current) return;
      const next = applyDesk(data, data?.record);
      if (uploadOnly && selectedId === row.id) {
        const nextOutputs = deskIterations(next);
        setSelectedId(nextOutputs[nextOutputs.length - 1]?.id || null);
      }
      toast(slot === "source" ? "Source image removed." : "Reference image removed.", "ok");
    } catch (err) {
      toast(apiDetail(err, "Could not remove that image."), "err");
    } finally {
      clearBusy();
    }
  };

  const generationById = (generationId) => iters.find((item) => item.id === generationId) || generations(record).find((item) => item.id === generationId);

  const artiPanel = (
    <ArtiVoicePanel
      talk={talk}
      onTalkChange={setTalk}
      messages={messages}
      busy={busy}
      busyKind={busyKind}
      canGenerate={Boolean(prompt.trim()) && Boolean(record?.id)}
      canEdit={canEdit}
      canReference={Boolean(selected)}
      micSupported={micSupported}
      listening={listening}
      speaking={artiSpeaking}
      voiceOn={voiceOn}
      onVoiceToggle={() => setVoiceOn((value) => !value)}
      line={artiLine}
      onMic={dictateArti}
      onSubmit={() => submitArtiCommand()}
      onGenerate={(event) => generate(event, "generate")}
      onEdit={(event) => generate(event, "edit")}
      onReference={() => promoteAsReference(selected)}
      onSendToPrompt={sendPendingToPrompt}
      pendingPrompt={pendingPrompt}
      suggestGenerate={suggestGenerate}
      compact={Boolean(record)}
      bar
    />
  );

  if (error) {
    return (
      <div className="fld-page" data-testid="forgelink-desk" data-edit-name="Design Desk">
        <p className="fld-empty">{String(error)}</p>
        <Link className="btn" to="/linkforge">Back to {FORGELINC_NAME}</Link>
      </div>
    );
  }

  const startMeshControl = (
    <label className="btn fld-start-mesh" data-testid="forgelink-desk-start-mesh" title="STL, GLB, or 3MF. Skips the idea and image steps. Image sources stay on a product.">
      Start from 3D file
      <input
        type="file"
        accept=".stl,.glb,.gltf,.3mf,model/stl,model/gltf-binary,model/gltf+json,model/3mf"
        disabled={busy}
        data-testid="forgelink-desk-start-mesh-file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          startFromMesh(file ? [file] : []);
        }}
      />
    </label>
  );

  if (!recordId) {
    const selectedRows = records.filter((row) => selectedIds.includes(row.id));
    return (
      <div className="fld-page is-picker" data-testid="forgelink-desk" data-edit-name="Design Desk">
        <header className="fld-picker-head">
          <h1>Products</h1>
          <div className="fld-picker-actions">
            {selectedRows.length ? (
              <button type="button" className="fld-quiet" disabled={busy} onClick={() => askDelete(selectedRows)} data-testid="forgelink-desk-delete-selected">
                Delete {selectedRows.length}
              </button>
            ) : null}
            {startMeshControl}
            <Link className="fld-quiet" to="/linkforge">Pipeline</Link>
          </div>
        </header>
        {artiPanel}
        {startError ? <p className="fld-error" data-testid="forgelink-desk-start-mesh-error">{startError}</p> : null}
        <div className="fld-picker-scroll">
          {records.length ? (
            <div className="fld-picker">
              {records.map((row) => {
                const count = deskIterations(row).length;
                const stage = pipelineStage(row);
                return (
                  <article className="fld-card" key={row.id} data-testid="forgelink-desk-card">
                    <label className="fld-card-check">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => setSelectedIds((current) => (current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id]))}
                        aria-label={`Select ${row.title}`}
                        data-testid="forgelink-desk-select"
                      />
                    </label>
                    <button type="button" className="fld-card-open" onClick={() => setParams({ record: row.id })}>
                      <span className="fld-card-media">
                        <DeskCardMedia record={row} />
                      </span>
                      <span className="fld-card-copy">
                        <b>{row.title}</b>
                        <small>{pipelineStageLabel(stage)} · {count} iteration{count === 1 ? "" : "s"}</small>
                      </span>
                    </button>
                    <span className="fld-card-tools">
                      <button type="button" disabled={busy} onClick={() => openEditor(row)} aria-label={`Edit ${row.title}`} title="Edit" data-testid="forgelink-desk-edit">
                        <Pencil size={14} />
                      </button>
                      <button type="button" disabled={busy} onClick={() => askDelete([row])} aria-label={`Delete ${row.title}`} title="Delete" data-testid="forgelink-desk-delete">
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="fld-empty">No products yet. Create one on the {FORGELINC_NAME} hub, then open Design Desk.</p>
          )}
        </div>
        {editor ? (
          <div className="fld-dialog-back" role="presentation">
            <form className="fld-dialog" role="dialog" aria-labelledby="fld-edit-title" onSubmit={saveEditor} data-testid="forgelink-desk-info-dialog">
              <h2 id="fld-edit-title">Edit product</h2>
              <label>
                <small>Title</small>
                <input value={editTitle} onChange={(event) => setEditTitle(event.target.value)} maxLength={200} required data-testid="forgelink-desk-info-title" />
              </label>
              <label>
                <small>Description</small>
                <textarea value={editDescription} onChange={(event) => setEditDescription(event.target.value)} maxLength={4000} rows={4} data-testid="forgelink-desk-info-description" />
              </label>
              <label>
                <small>Stage</small>
                <select value={editStage} onChange={(event) => setEditStage(event.target.value)} data-testid="forgelink-desk-info-stage">
                  {PIPELINE_STAGES.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
                </select>
              </label>
              {editError ? <p className="fld-error">{editError}</p> : null}
              <div className="fld-dialog-actions">
                <button type="button" className="btn" disabled={busy} onClick={() => setEditor(null)}>Cancel</button>
                <button type="submit" className="btn primary" disabled={busy} data-testid="forgelink-desk-info-save">Save</button>
              </div>
            </form>
          </div>
        ) : null}
        {pendingDelete.length ? (
          <div className="fld-dialog-back" role="presentation">
            <div className="fld-dialog" role="dialog" aria-labelledby="fld-delete-title" data-testid="forgelink-desk-delete-dialog">
              <h2 id="fld-delete-title">Delete {pendingDelete.length === 1 ? pendingDelete[0].title : `${pendingDelete.length} products`}?</h2>
              <p>{pendingDelete.length === 1 ? "This product leaves the Design Desk and the ForgeLinc list." : "These products leave the Design Desk and the ForgeLinc list."} Uploaded files stay on disk. This does not remove other products.</p>
              {deleteError ? <p className="fld-error">{deleteError}</p> : null}
              <div className="fld-dialog-actions">
                <button type="button" className="btn" disabled={busy} onClick={() => setPendingDelete([])} data-testid="forgelink-desk-delete-cancel">Cancel</button>
                <button type="button" className="btn primary" disabled={busy} onClick={confirmDelete} data-testid="forgelink-desk-delete-confirm">Delete</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (!record) {
    return <div className="fld-page" data-edit-name="Design Desk"><div className="fld-empty">Opening Design Desk…</div></div>;
  }

  const previewPath = selected ? `/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${selected.id}/preview` : null;
  const selectedIndex = iters.findIndex((row) => row.id === selected?.id);
  const previousIteration = selectedIndex > 0 ? iters[selectedIndex - 1] : null;
  const comparePath = previousIteration ? `/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${previousIteration.id}/preview` : "";
  const shownRenderer = badgeFor(renderer, selected || primarySource || reference, forceStub);
  const liveConfigured = !forceStub && renderer.mode === "live";
  const sourceIsArmed = Boolean(editFromSource && (primarySource || reference));

  const withPreview = (row) => ({
    ...row,
    previewPath: `/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${row.id}/preview`,
  });
  const sourceItems = sources.map(withPreview);
  const referenceItems = references.map(withPreview);

  return (
    <div className={`fld-page is-work${viewerFocus ? " is-focus" : ""}${inputOpen ? " is-input-open" : ""}`} data-testid="forgelink-desk" data-edit-name="Design Desk">
      <header className="fld-top">
        <div>
          <small>{FORGELINC_NAME.toUpperCase()} · DESIGN DESK</small>
          <h1>{record.title}</h1>
        </div>
        <div className="fld-top-actions">
          <span className={`pill ${pillTone(shownRenderer.mode)}`} data-testid="forgelink-desk-renderer">{shownRenderer.label}</span>
          <button type="button" className="btn" onClick={() => setAvatarsHidden((value) => !value)} data-testid="forgelink-desk-avatars">
            {avatarsHidden ? "Show avatars" : "Hide avatars"}
          </button>
          {startMeshControl}
          <Link className="btn" to={hubProductPath(recordId)}><ArrowLeft size={13} /> Pipeline</Link>
          {currentId ? <Link className="btn primary" to={hubProductPath(recordId, { view: "render" })} data-testid="forgelink-desk-to-render">Open Render path <ArrowRight size={13} /></Link> : null}
          {record?.id ? <Link className="btn" to={loopHubPath(record.id, "ai_studio")} data-testid="forgelink-desk-to-studio">Handoff to AI Studio <ArrowRight size={13} /></Link> : null}
        </div>
      </header>

      {artiPanel}

      <div className="fld-layout">
        <button type="button" className="fld-rail" onClick={() => setInputOpen((value) => !value)} data-testid="forgelink-desk-input-toggle">
          {inputOpen ? "Hide input" : "Input"}
        </button>
        <section className={`fld-input${inputOpen ? " is-open" : ""}`} data-testid="forgelink-desk-input" data-edit-name="Design Desk input">
          <header>
            <span>
              <b>Input</b>
              <small>Prompt, style, sources, and references</small>
            </span>
            <Sparkles size={15} />
          </header>
          <form onSubmit={(event) => generate(event, "generate")}>
            <div className="fld-prompt-row">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Black hoodie with BOMBERS in bold varsity letters across the chest"
                data-testid="forgelink-desk-prompt"
              />
              <button
                className={`fld-mic${listening ? " is-on" : ""}`}
                type="button"
                onClick={dictatePrompt}
                disabled={busy}
                title={micSupported ? (listening ? "Stop listening" : "Speak prompt (appends)") : "Voice input is not available in this browser"}
                aria-label={micSupported ? "Voice to text" : "Voice input not supported"}
                data-testid="forgelink-desk-mic"
              >
                <Mic size={15} />
              </button>
            </div>
            <div className="fld-extras">
              <label>
                <small>Text on product</small>
                <input
                  value={textOverlay}
                  onChange={(e) => setTextOverlay(e.target.value)}
                  placeholder="BOMBERS"
                  maxLength={48}
                  data-testid="forgelink-desk-text"
                />
              </label>
              <label>
                <small>Style</small>
                <select
                  value={stylePreset}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStylePreset(value);
                    if (value !== DESK_STYLE_CUSTOM) setStyleCustom("");
                  }}
                  data-testid="forgelink-desk-style"
                >
                  <option value="">Choose a style</option>
                  {DESK_STYLE_PRESETS.map((row) => (
                    <option value={row.id} key={row.id}>{row.label}</option>
                  ))}
                  <option value={DESK_STYLE_CUSTOM}>Custom…</option>
                </select>
              </label>
            </div>
            {stylePreset === DESK_STYLE_CUSTOM ? (
              <label className="fld-style-custom">
                <small>Custom style</small>
                <input
                  value={styleCustom}
                  onChange={(e) => setStyleCustom(e.target.value)}
                  placeholder="Describe the look"
                  maxLength={160}
                  data-testid="forgelink-desk-style-custom"
                />
              </label>
            ) : null}
            {startError ? <p className="fld-error" data-testid="forgelink-desk-start-mesh-error">{startError}</p> : null}
            {meshSessionSource(record) ? (
              <p className="fld-mesh-source" data-testid="forgelink-desk-mesh-source">
                3D source: {meshSessionSource(record).filename}
              </p>
            ) : null}
            <div className="fld-inputs">
              <InputStrip
                title="Sources"
                hint="Drop images or an iteration"
                items={sourceItems}
                busy={busy}
                armed={sourceIsArmed}
                testId="forgelink-desk-source"
                fileTestId="forgelink-desk-source-file"
                onFiles={(files) => uploadInputFiles(files, "source")}
                onDropGeneration={(generationId) => {
                  const row = generationById(generationId);
                  if (row) promoteAsSource(row);
                }}
                onPrimary={(row) => promoteAsSource(row)}
                onRemove={(row) => removeInput(row, "source")}
              />
              <InputStrip
                title="References"
                hint="Drop images or an iteration"
                items={referenceItems}
                busy={busy}
                testId="forgelink-desk-references"
                fileTestId="forgelink-desk-reference"
                onFiles={(files) => uploadInputFiles(files, "reference")}
                onDropGeneration={(generationId) => {
                  const row = generationById(generationId);
                  if (row) promoteAsReference(row);
                }}
                onPrimary={(row) => promoteAsReference(row)}
                onRemove={(row) => removeInput(row, "reference")}
              />
            </div>
            {generateError ? (
              <div className="fld-error" data-testid="forgelink-desk-error">
                <p>{generateError}</p>
                {lastFailedMode ? (
                  <button className="btn small" type="button" disabled={busy} onClick={(event) => generate(event, lastFailedMode)}>
                    Retry
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="fld-compose-actions">
              <button
                className={canEdit ? "btn primary" : "btn"}
                type="button"
                disabled={!prompt.trim() || busy || !canEdit}
                onClick={(event) => generate(event, "edit")}
                data-testid="forgelink-desk-continue"
              >
                <ImagePlus size={13} /> {busyKind === "edit" ? (liveConfigured ? "Calling OpenAI edits…" : "Editing image…") : "Continue editing"}
              </button>
              <button
                className={canEdit ? "btn" : "btn primary"}
                type="submit"
                disabled={!prompt.trim() || busy}
                data-testid="forgelink-desk-generate"
              >
                <Wand2 size={13} /> {busyKind === "generate" ? (liveConfigured ? "Calling OpenAI…" : "Generating…") : "Generate"}
              </button>
              <button className="btn" type="button" disabled={!selected || busy} onClick={refinePrompt} data-testid="forgelink-desk-refine">
                Refine prompt
              </button>
              <button className="btn" type="button" disabled={!selected || busy || selected.id === currentId} onClick={() => selectIteration(selected)} data-testid="forgelink-desk-confirm">
                Set as current design
              </button>
            </div>
          </form>
          <button className="btn small fld-cad-toggle" type="button" onClick={() => setShowUpload((open) => !open)}>
            {showUpload ? "Hide CAD / file shelf" : "CAD / design file"}
          </button>
          {showUpload ? (
            <div className="fld-upload">
              <DesignAttachForm businessKey={BUSINESS_KEY} record={record} onAttached={async (next) => { setRecord(next); toast("File saved as current design.", "ok"); }} />
            </div>
          ) : null}
        </section>

        <section className="fld-output" data-testid="forgelink-desk-output" data-edit-name="Design Desk output">
          <div className="fld-canvas" ref={viewerRef} data-edit-name={outputMode === "mesh" ? "Design Desk 3D viewer" : "Design Desk viewer"}>
            <header>
              <span>
                <b>{outputMode === "mesh" ? "3D inspect" : selected ? "Selected iteration" : "No output yet"}</b>
                <small>{outputMode === "mesh" ? "Orbit, paint, then send the marked views" : selected ? `${selected.renderer || "stub"} · ${formatWhen(selected.created_at)}${selected.mode === "edit" ? " · image edit" : ""}` : "Generate or Continue editing from the source"}</small>
              </span>
              <button type="button" className="btn small" onClick={toggleViewerFocus} data-testid="forgelink-desk-focus">
                {viewerFocus ? "Exit focus" : "Focus"}
              </button>
              <div className="fld-view-switch" role="tablist" aria-label="Output view">
                <button type="button" role="tab" aria-selected={outputMode === "image"} className={outputMode === "image" ? "is-on" : ""} onClick={() => setOutputMode("image")} data-testid="forgelink-desk-view-image">Image</button>
                <button type="button" role="tab" aria-selected={outputMode === "mesh"} className={outputMode === "mesh" ? "is-on" : ""} onClick={() => setOutputMode("mesh")} data-testid="forgelink-desk-view-mesh">3D</button>
              </div>
              {selected && selected.id === currentId ? <span className="pill cyan">current design</span> : null}
            </header>
            <div className={`fld-canvas-body${busy && (busyKind === "generate" || busyKind === "edit") ? " is-busy" : ""}${outputMode === "mesh" ? " is-mesh" : ""}`} data-testid="forgelink-desk-canvas">
              {outputMode === "mesh" ? null : selected ? (
                <ImageInspectViewport
                  path={previewPath}
                  comparePath={comparePath}
                  alt={snippet(selected.prompt, 80)}
                  canEdit={canEdit}
                  busy={busy}
                  onEditArea={(text) => {
                    setPrompt(text);
                    generate(null, "edit", text);
                  }}
                />
              ) : (
                <div className="fld-empty">Outputs land here. Upload a source on the left, then Continue editing or Generate.</div>
              )}
              <MeshInspectViewport record={record} active={outputMode === "mesh"} onRecord={setRecord} preferMeshId={meshSessionSource(record)?.artifact_id} />
            </div>
          </div>

          <div className={`fld-strip-wrap${stripOpen ? "" : " is-collapsed"}`}>
            <header>
              <span>
                <b>Iterations</b>
                <small>{iters.length ? `${iters.length} output${iters.length === 1 ? "" : "s"}` : "Generated and edited takes only"}</small>
              </span>
              <button type="button" className="btn small" onClick={() => setStripOpen((value) => !value)} data-testid="forgelink-desk-strip-toggle">
                {stripOpen ? "Hide strip" : "Show strip"}
              </button>
            </header>
            <div className="fld-strip" data-testid="forgelink-desk-strip">
              {iters.length ? iters.map((row) => {
                const isCurrent = row.id === currentId;
                const isSelected = row.id === selected?.id;
                const isSource = sources.some((item) => item.id === row.id);
                const isReference = references.some((item) => item.id === row.id);
                return (
                  <div
                    key={row.id}
                    className={`fld-iter${isCurrent ? " is-current" : ""}${isSelected ? " is-selected" : ""}${isSource ? " is-source" : ""}${isReference ? " is-reference" : ""}`}
                    data-testid="forgelink-desk-iteration"
                    draggable={!busy}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/forgelink-generation", row.id);
                      event.dataTransfer.setData("text/plain", row.id);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <button type="button" className="fld-iter-main" onClick={() => selectIteration(row)} disabled={busy}>
                      <AuthImage path={`/businesses/${BUSINESS_KEY}/records/${record.id}/generations/${row.id}/preview`} alt={snippet(row.prompt, 40)} placeholderClassName="fld-ph" />
                      <div>
                        <b>{snippet(row.prompt, 54)}</b>
                        <small>{row.renderer || "stub"}{isCurrent ? " · current" : ""}{isSource ? " · source" : ""}{isReference ? " · reference" : ""}{iterationKindLabel(row) ? ` · ${iterationKindLabel(row)}` : ""}{row.parent_id ? " · chained" : ""}</small>
                      </div>
                    </button>
                    <div className="fld-iter-tools">
                      <ScoreStars value={row.score} disabled={busy} onChange={(score) => scoreIteration(row, score)} />
                      <button
                        type="button"
                        className="fld-iter-ref"
                        disabled={busy || isSource}
                        title="Use as reference"
                        data-testid="forgelink-desk-use-reference"
                        onClick={(event) => {
                          event.stopPropagation();
                          promoteAsReference(row);
                        }}
                      >
                        Use as reference
                      </button>
                      <button
                        type="button"
                        className="fld-iter-delete"
                        disabled={busy}
                        title="Delete iteration"
                        aria-label="Delete iteration"
                        data-testid="forgelink-desk-delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteIteration(row);
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              }) : <p className="fld-empty">No iterations yet. Source uploads stay in the input slot.</p>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
